/**
 * CustomerAlarms — customer-facing global Alarms hub at `/alarms`.
 *
 * EPIC #225 sub 6/10. Mirrors the on-site `/alarms` IA (Hybrid A+B from
 * workspace spec §6) but scoped to the customer's accessible sites.
 *
 * Layout (single column, no right rail — spec §6.1):
 *   1. Page header
 *   2. <AlarmFilterCluster expanded>     — URL-param persistence built-in
 *   3. <AlarmRollupBanner site>           — counts derived from filtered list
 *   4. Active alarms section              — block-grouped, always-expanded
 *   5. <RunningLogCard last=20>           — WS-fed ring buffer
 *
 * Real-time behaviour:
 *   useGlobalAlarmSubscription joins every accessible site's WS room and
 *   invalidates the React Query key `['alarms', 'all']` on each
 *   `alarm:transition`. The page also keeps a 20-event ring buffer for the
 *   running log (avoids a new cross-customer history endpoint for v1).
 *
 * Pane-of-glass: read-only. No ack/clear/resolve actions on this surface.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlarmFilterCluster,
  AlarmRollupBanner,
  AlarmRow,
  RunningLogCard,
  DEFAULT_FILTER_VALUE,
  toDisplayRow,
  mapEquipmentChip,
  alarmBucket,
  deriveSeverityCounts,
  type FilterValue,
  type SeverityCounts,
} from '../components/alarms/primitives';
import { AlarmSlideOver } from '../components/alarms/AlarmSlideOver';
import {
  decodeAlarmIdentity,
  encodeAlarmIdentity,
} from '../components/alarms/alarm-deep-link';
import { useCustomerAlarms } from '../hooks/useCustomerAlarms';
import { useGlobalAlarmSubscription } from '../hooks/useGlobalAlarmSubscription';
import { useSites, type Site } from '../hooks/useSites';
import { getSocket } from '../services/socket';
import type { ActiveAlarm, AlarmHistoryEvent } from '../types/alarm.types';
import type { AlarmTransitionEvent } from '../hooks/useAlarmSubscription';

const RUNNING_LOG_MAX = 20;
/** Window in seconds matching the FilterValue.timeRange chips (per spec §6.2). */
const TIME_RANGE_SECONDS: Record<FilterValue['timeRange'], number> = {
  '1h': 3600,
  '4h': 4 * 3600,
  '24h': 24 * 3600,
  '7d': 7 * 86400,
  '30d': 30 * 86400,
};

export function CustomerAlarms() {
  useGlobalAlarmSubscription();

  const [filter, setFilter] = useState<FilterValue>(DEFAULT_FILTER_VALUE);
  const [searchParams, setSearchParams] = useSearchParams();
  const sitesQ = useSites();

  // URL is the source of truth for slide-over open/closed state. This makes
  // deep-links from the bell popover (?alarm=<site>:<device>:<cond>) work for
  // free, lets the browser back/forward navigate between alarms naturally,
  // and preserves the open panel across refresh.
  const selectedIdentity = useMemo(
    () => decodeAlarmIdentity(searchParams.get('alarm')),
    [searchParams],
  );

  const openAlarm = useCallback(
    (a: ActiveAlarm) => {
      const next = new URLSearchParams(searchParams);
      next.set(
        'alarm',
        encodeAlarmIdentity({
          siteId: a.site_id,
          deviceId: a.device_id,
          conditionKey: a.condition_key,
        }),
      );
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const closeSlideOver = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('alarm');
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  // Server-side pre-filter. The client-side matchesFilter is the display source
  // of truth; this only narrows the payload.
  //
  // When both active tabs are selected, status is undefined (API returns all
  // uncleared rows; client narrows). When one bucket is selected its value is
  // passed through directly (AlarmRowStatus matches the API status parameter).
  //
  // CRITICAL: raised_after is ONLY sent when History is the sole selected bucket.
  // It MUST NOT be sent for needs_attention / acknowledged — it would hide active
  // alarms older than the window, which operators must never miss.
  //
  // isHistoryOnly gates both raised_after (API) and showTimeFilter (UI chips) —
  // a single declaration makes it structurally impossible for the two to diverge.
  const isHistoryOnly = filter.status.length === 1 && filter.status[0] === 'history';

  const apiFilters = useMemo(() => {
    // Computed from the time-range key (not Date.now()) so the React Query cache
    // key changes when the user picks a different window, not on every tick.
    //
    // Still gated on isHistoryOnly, matching the chips' visibility: in a
    // blended selection the chips are hidden, so sending the (default 24h)
    // window would silently cap history to a period the user never chose.
    const raised_after = isHistoryOnly
      ? new Date(Date.now() - TIME_RANGE_SECONDS[filter.timeRange] * 1000).toISOString()
      : undefined;
    return {
      // The full array, not a collapsed scalar — the hook decides which
      // source(s) to query from it. Collapsing here meant a 2-bucket selection
      // resolved to `undefined`, which the server reads as "all active", so
      // History rows vanished with no error shown (#628).
      statuses: filter.status,
      severity: filter.severity.length === 1 ? (filter.severity[0] as string) : undefined,
      raised_after,
    };
  }, [isHistoryOnly, filter.status, filter.severity, filter.timeRange]);

  const alarmsQ = useCustomerAlarms(apiFilters);

  const filtered = useMemo<ActiveAlarm[]>(() => {
    const all = (alarmsQ.data?.alarms ?? []) as ActiveAlarm[];
    return all.filter((a) => matchesFilter(a, filter));
  }, [alarmsQ.data, filter]);

  const counts = useMemo<SeverityCounts>(() => deriveSeverityCounts(filtered), [filtered]);

  const groups = useMemo(
    () => groupBySiteAndBlock(filtered, sitesQ.data?.sites ?? []),
    [filtered, sitesQ.data],
  );

  // WS-fed ring buffer for the running log. Updated by a side-effect hook
  // that listens directly to alarm:transition (the global subscription hook
  // handles room-joining and React Query invalidation; this only collects
  // events for the lifecycle log).
  const runningEvents = useRunningLogBuffer();

  const filteredRunningEvents = useMemo(
    () => filterRunningEvents(runningEvents, filter),
    [runningEvents, filter],
  );

  return (
    <div
      className="flex flex-col gap-4 p-4 max-w-5xl mx-auto w-full"
      data-testid="customer-alarms-page"
    >
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ems-text-primary">Alarms</h1>
        {alarmsQ.isError && (
          <button
            type="button"
            onClick={() => alarmsQ.refetch()}
            className="text-xs px-3 py-1 rounded border border-ems-border text-ems-text-secondary hover:text-ems-text-primary"
          >
            Retry
          </button>
        )}
      </header>

      <AlarmFilterCluster
        value={filter}
        onChange={setFilter}
        variant="expanded"
        showTimeFilter={isHistoryOnly}
      />

      <AlarmRollupBanner scope="site" name="All sites" counts={counts} />

      {alarmsQ.data?.historyTruncated && (
        <HistoryTruncationNotice isHistoryOnly={isHistoryOnly} />
      )}

      <section data-testid="active-alarms-section" className="flex flex-col gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-ems-text-secondary">
          Active alarms
        </h2>
        {alarmsQ.isLoading ? (
          <p className="text-xs text-ems-text-muted py-4 text-center">Loading…</p>
        ) : groups.length === 0 ? (
          <EmptyState
            reason={alarmsQ.isError ? 'error' : 'no-match'}
            onClear={() => setFilter(DEFAULT_FILTER_VALUE)}
          />
        ) : (
          groups.map((g) => <BlockGroup key={g.key} group={g} onAlarmClick={openAlarm} />)
        )}
      </section>

      <RunningLogCard events={filteredRunningEvents} title="Recent events" />

      <AlarmSlideOver identity={selectedIdentity} onClose={closeSlideOver} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Filtering helpers (kept in-file — small, page-specific, not reusable)
// ────────────────────────────────────────────────────────────────────────

function matchesFilter(a: ActiveAlarm, f: FilterValue): boolean {
  if (f.severity.length > 0 && !f.severity.includes(a.severity)) return false;
  if (f.status.length > 0 && !f.status.includes(alarmBucket(a))) return false;
  if (f.equipment.length > 0) {
    const chip = mapEquipmentChip(a.device_type);
    if (!chip || !f.equipment.includes(chip)) return false;
  }
  if (f.search) {
    const q = f.search.toLowerCase();
    const hay = `${a.condition_label} ${a.device_id} ${a.condition_key}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

interface Group {
  key: string;
  siteId: string;
  siteName: string;
  blockId: string | null;
  rows: ActiveAlarm[];
  highestSeverity: 'critical' | 'warning' | 'info';
}

const SEVERITY_RANK: Record<string, number> = { critical: 3, warning: 2, info: 1 };

function groupBySiteAndBlock(rows: ActiveAlarm[], sites: Site[]): Group[] {
  // Build a site_code → display name map. alarms.active_alarms.site_id is
  // the wire site_code (e.g. 'HONDO-01'); config.sites.site_code is the same.
  const codeToName = new Map<string, string>();
  for (const s of sites) {
    if (s.site_code) codeToName.set(s.site_code, s.name);
  }

  const map = new Map<string, Group>();
  for (const r of rows) {
    const blockKey = r.block_id ?? '_no-block';
    const key = `${r.site_id}:${blockKey}`;
    const existing = map.get(key);
    if (existing) {
      existing.rows.push(r);
      if ((SEVERITY_RANK[r.severity] ?? 0) > SEVERITY_RANK[existing.highestSeverity]) {
        existing.highestSeverity = r.severity;
      }
    } else {
      map.set(key, {
        key,
        siteId: r.site_id,
        siteName: codeToName.get(r.site_id) ?? r.site_id,
        blockId: r.block_id,
        rows: [r],
        highestSeverity: r.severity,
      });
    }
  }

  // Sort: severity desc, then site name asc, then block name asc — spec §6.3
  // ("severity desc, then by name asc").
  return Array.from(map.values()).sort((a, b) => {
    const sevDiff = SEVERITY_RANK[b.highestSeverity] - SEVERITY_RANK[a.highestSeverity];
    if (sevDiff !== 0) return sevDiff;
    if (a.siteName !== b.siteName) return a.siteName.localeCompare(b.siteName);
    return (a.blockId ?? '').localeCompare(b.blockId ?? '');
  });
}

// ────────────────────────────────────────────────────────────────────────
// Subcomponents
// ────────────────────────────────────────────────────────────────────────

function BlockGroup({
  group,
  onAlarmClick,
}: {
  group: Group;
  onAlarmClick: (a: ActiveAlarm) => void;
}) {
  return (
    <div className="border border-ems-border rounded">
      <div
        className="flex items-center justify-between px-3 py-2 bg-ems-bg-card border-b border-ems-border"
        data-testid="block-group-header"
        data-site-id={group.siteId}
        data-block-id={group.blockId ?? ''}
      >
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-[10px] uppercase tracking-wider text-ems-text-muted">
            {group.siteName}
          </span>
          <span className="text-sm font-semibold text-ems-text-primary truncate">
            {group.blockId ?? 'Other devices'}
          </span>
          <span className="text-xs text-ems-text-muted">· {group.rows.length} active</span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 p-2">
        {group.rows.map((r) => (
          <AlarmRow
            key={`${r.site_id}:${r.device_id}:${r.condition_key}`}
            alarm={toDisplayRow(r)}
            onClick={() => onAlarmClick(r)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Non-modal notice shown when the server capped the History response (#639).
 *
 * The hub's History bucket returns at most 1000 rows, newest first. POD clears
 * ~712 alarms/day, so the 7d (~5,000) and 30d (~21,000) windows WILL cap —
 * and a capped list is visually identical to a complete one. An operator asking
 * "did this condition recur last month?" would otherwise see a list that stops
 * at roughly the last 34 hours and read it as the whole month.
 *
 * The copy says History "is capped" rather than "showing 1,000" on purpose. The
 * cap is applied server-side BEFORE the client-side severity / equipment /
 * search filters in `matchesFilter`, so the rendered count is routinely lower
 * than 1,000 and can be zero — at which point "showing the 1,000 most recent"
 * would sit next to "No alarms match the current filter" and read as a
 * contradiction. Describing the cap stays true at any filter state, and it is
 * the more useful signal: three critical rows drawn from a capped population is
 * exactly the case an operator must not read as "only three occurred".
 *
 * The guidance branches because the actionable remedy differs by selection:
 * the time chips are only rendered when History is the sole bucket
 * (`showTimeFilter={isHistoryOnly}`). In a blended selection there is no window
 * control on screen and the server applies its 30-day default, so telling the
 * operator to "narrow the time range" would point at a control that isn't
 * there. Pointing them at History-on-its-own is the step that actually reaches
 * the chips.
 */
function HistoryTruncationNotice({ isHistoryOnly }: { isHistoryOnly: boolean }) {
  return (
    <div
      role="status"
      data-testid="history-truncation-notice"
      className="text-xs text-ems-amber border border-ems-amber/30 rounded p-3"
    >
      {isHistoryOnly ? (
        <>
          History is capped at the 1,000 most recent cleared alarms in this window.
          Narrow the time range to see the full set.
        </>
      ) : (
        <>
          History is capped at the 1,000 most recent cleared alarms from the last 30
          days. Select History on its own to choose a shorter time range.
        </>
      )}
    </div>
  );
}

function EmptyState({
  reason,
  onClear,
}: {
  reason: 'no-match' | 'error';
  onClear: () => void;
}) {
  if (reason === 'error') {
    return (
      <div className="text-xs text-ems-red border border-ems-red/30 rounded p-3">
        Could not load alarms. The Retry button (top right) will fetch again.
      </div>
    );
  }
  return (
    <div className="text-xs text-ems-text-muted border border-ems-border rounded p-3 flex items-center justify-between gap-3">
      <span>No alarms match the current filter.</span>
      <button
        type="button"
        onClick={onClear}
        className="text-ems-blue hover:underline focus:outline-none focus:ring-2 focus:ring-ems-blue/40 rounded"
      >
        Clear filters
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Running-log ring buffer
// ────────────────────────────────────────────────────────────────────────

/**
 * Maintains a 20-event ring buffer of recent `alarm:transition` events for
 * the RunningLogCard. Side-effect-only — does NOT trigger React Query
 * invalidation (useGlobalAlarmSubscription owns that).
 *
 * Why a ring buffer instead of a fetch: there is no cross-customer alarm
 * history endpoint in v1 (the per-site `/alarms/history` is per-site only).
 * The WS stream is the canonical real-time source, and 20 events is small
 * enough to hold in memory across page navigations.
 */
function useRunningLogBuffer(): AlarmHistoryEvent[] {
  const [events, setEvents] = useState<AlarmHistoryEvent[]>([]);
  const ringRef = useRef<AlarmHistoryEvent[]>([]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handle = (event: AlarmTransitionEvent) => {
      // Guard mirrors useGlobalAlarmSubscription — a malformed payload would
      // otherwise crash the handler, Socket.io would absorb the TypeError,
      // and the running-log card would silently freeze.
      if (!event?.identity) return;
      const log: AlarmHistoryEvent = {
        event_id: event.event_id,
        site_id: event.identity.site_id,
        device_id: event.identity.device_id,
        condition_key: event.identity.condition_key,
        device_type: '',
        block_id: null,
        transition: event.transition,
        severity: event.severity,
        category: 'fault',
        ts: event.ts,
        condition_label: event.condition_label,
        value: null,
        metadata: {},
        lifecycle: null,
      };
      ringRef.current = [log, ...ringRef.current].slice(0, RUNNING_LOG_MAX);
      setEvents(ringRef.current);
    };

    socket.on('alarm:transition', handle);
    return () => {
      socket.off('alarm:transition', handle);
    };
  }, []);

  return events;
}

function filterRunningEvents(
  events: AlarmHistoryEvent[],
  f: FilterValue,
): AlarmHistoryEvent[] {
  const cutoffMs = Date.now() - TIME_RANGE_SECONDS[f.timeRange] * 1000;
  return events.filter((e) => {
    if (new Date(e.ts).getTime() < cutoffMs) return false;
    if (f.severity.length > 0 && !f.severity.includes(e.severity)) return false;
    if (f.search) {
      const q = f.search.toLowerCase();
      const hay = `${e.condition_label} ${e.device_id} ${e.condition_key}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

